import { gql } from "generated-graphql";

export const EPISODIC_EPA_FILING_FRAGMENT = gql(`
  fragment EpisodicEpaFilingFields on EpisodicEpaFiling {
    handlerId
    activityLocation
    seqNumber
    receiveDate
    eventType
    startDate
    endDate
    projectTypes
    otherProjectDescription
    wastes {
      description
      estimatedQuantity
      wasteCodes
      stateWasteCodes
    }
  }
`);

/**
 * Shared row shape for the episodic events ledger. `submissions` is the
 * EPA-notification filing history (newest first); `containers` is the set
 * attached now, which the finalize and lapse controls key on;
 * `containerHistory` is every container on the event's record, which the page
 * displays; `relief` says whether the facility's state adopted subpart L.
 */
export const EPISODIC_EVENT_ITEM_FRAGMENT = gql(`
  fragment EpisodicEventItem on WasteEpisodicEvent {
    id
    facilityId
    type
    status
    reason
    startDate
    endDate
    accumulationDeadline
    declarationsWritable
    relief {
      isAvailable
      reason
    }
    notices {
      id
      kind
      noticeDate
      method
      note
      document {
        id
        title
        fileExtension
      }
      epaFiling {
        ...EpisodicEpaFilingFields
      }
    }
    submissions {
      id
      title
      status
      dueDate
      filedAt
    }
    containers {
      id
      status
      amount
      amountUnit
      archivedAt
    }
    containerHistory {
      isAttached
      attachedAt
      firstFillAt
      addedLb
      shippedLb
      lastRemoval
      container {
        id
        containerLabel
        status
        accumulationArea {
          id
          name
          type
        }
        wasteStream {
          id
          name
        }
      }
    }
    # Named in the delete confirm: deleting the event returns these pounds to status.
    manifestExemptions {
      totalExemptPounds
    }
  }
`);

/** Facility-scoped year ledger — filterable by status, paginated. */
export const FACILITY_EPISODIC_EVENTS_QUERY = gql(`
  query FacilityEpisodicEvents(
    $facilityId: ID!
    $status: WasteEpisodicEventStatus
    $startedOnOrAfter: Date
    $startedBefore: Date
    $page: Int
    $pageSize: Int
  ) {
    facility(id: $facilityId) {
      id
      name
      generationDrivenStatus
      colocationWasteFacilityId
      colocationWasteFacility {
        id
        name
      }
      episodicEvents(
        status: $status
        startedOnOrAfter: $startedOnOrAfter
        startedBefore: $startedBefore
        page: $page
        pageSize: $pageSize
      ) {
        items {
          ...EpisodicEventItem
        }
        count
      }
    }
  }
`);

export const EPISODIC_EVENT_VALIDATOR_QUERY = gql(`
  query EpisodicEventInputValidator($input: EpisodicEventInput!, $currentEventId: ID) {
    episodicEventInputValidator(input: $input, currentEventId: $currentEventId) {
      ...issue
    }
  }
`);

export const CREATE_EPISODIC_EVENT_MUTATION = gql(`
  mutation CreateEpisodicEvent($input: CreateEpisodicEventInput!) {
    createEpisodicEvent(input: $input) {
      ...EpisodicEventItem
    }
  }
`);

export const UPDATE_EPISODIC_EVENT_MUTATION = gql(`
  mutation UpdateEpisodicEvent($input: UpdateEpisodicEventInput!) {
    updateEpisodicEvent(input: $input)
  }
`);

export const FINALIZE_EPISODIC_EVENT_MUTATION = gql(`
  mutation FinalizeEpisodicEvent($id: ID!) {
    finalizeEpisodicEvent(id: $id)
  }
`);

export const REOPEN_EPISODIC_EVENT_MUTATION = gql(`
  mutation ReopenEpisodicEvent($id: ID!) {
    reopenEpisodicEvent(id: $id)
  }
`);

export const DELETE_EPISODIC_EVENT_MUTATION = gql(`
  mutation DeleteEpisodicEvent($id: ID!) {
    deleteEpisodicEvent(id: $id)
  }
`);

/** Attaches a whole selection in one transaction — see the schema field: a
 * container the guard refuses leaves none of them attached. */
export const SET_CONTAINERS_EPISODIC_EVENT_MUTATION = gql(`
  mutation SetContainersEpisodicEvent($containerIds: [ID!]!, $episodicEventId: ID) {
    setContainersEpisodicEvent(containerIds: $containerIds, episodicEventId: $episodicEventId)
  }
`);

export const SET_CONTAINER_EPISODIC_EVENT_MUTATION = gql(`
  mutation SetContainerEpisodicEvent(
    $containerId: ID!
    $episodicEventId: ID
    $detachFromEpisodicEventId: ID
  ) {
    setContainerEpisodicEvent(
      containerId: $containerId
      episodicEventId: $episodicEventId
      detachFromEpisodicEventId: $detachFromEpisodicEventId
    )
  }
`);

/** The attach picker's options. The server decides attachability (see the
 * schema field), so the picker cannot drift from the attach guard. */
export const EPISODIC_ATTACHABLE_CONTAINERS_QUERY = gql(`
  query EpisodicAttachableContainers($eventId: ID!) {
    episodicEvent(id: $eventId) {
      id
      relief {
        isAvailable
        reason
      }
      attachableContainers {
        id
        containerLabel
        status
        accumulationArea {
          id
          name
        }
      }
    }
  }
`);

export const CREATE_EPISODIC_EVENT_SUBMISSION_MUTATION = gql(`
  mutation CreateEpisodicEventSubmission($eventId: ID!) {
    createEpisodicEventSubmission(eventId: $eventId)
  }
`);

export const UPSERT_EPISODIC_EVENT_NOTICE_MUTATION = gql(`
  mutation UpsertEpisodicEventNotice($input: UpsertEpisodicEventNoticeInput!) {
    upsertEpisodicEventNotice(input: $input) {
      id
      kind
      noticeDate
      method
      note
      document {
        id
        title
        fileExtension
      }
      epaFiling {
        ...EpisodicEpaFilingFields
      }
    }
  }
`);

export const DELETE_EPISODIC_EVENT_NOTICE_MUTATION = gql(`
  mutation DeleteEpisodicEventNotice($eventId: ID!, $kind: WasteEpisodicEventNoticeKind!) {
    deleteEpisodicEventNotice(eventId: $eventId, kind: $kind)
  }
`);

export const EPISODIC_EPA_FILING_CANDIDATES_QUERY = gql(`
  query EpisodicEpaFilingCandidates(
    $facilityId: ID!
    $type: WasteEpisodicEventType!
    $startDate: Date!
    $excludeEventId: ID
  ) {
    episodicEpaFilingCandidates(
      facilityId: $facilityId
      type: $type
      startDate: $startDate
      excludeEventId: $excludeEventId
    ) {
      typeMatches
      filing {
        ...EpisodicEpaFilingFields
      }
    }
  }
`);

export function eventRefetchQueries(containerTracking: boolean): string[] {
  return containerTracking
    ? ["FacilityEpisodicEvents", "FacilityAccumulationSummary"]
    : ["FacilityEpisodicEvents"];
}

export const EPISODIC_MANIFEST_EXEMPTION_FRAGMENT = gql(`
  fragment EpisodicManifestExemptionFields on EpisodicManifestExemption {
    id
    state
    declaredPounds
    changedSinceDeclared
    updatedAt
    updatedBy
  }
`);

export const EPISODIC_EVENT_MANIFESTS_QUERY = gql(`
  query EpisodicEventManifests($id: ID!) {
    episodicEvent(id: $id) {
      id
      manifestExemptions {
        totalExemptPounds
        calculatedExemptPounds
        declaredExemptPounds
        unmatchedShippedContainerCount
        epaEstimatedPounds
        filedWithEpa
        manifests {
          manifestTrackingNumber
          documentKind
          shippedDate
          exemptPounds
          streams {
            wasteStream {
              id
              name
            }
            state
            declarable
            claimedPounds
            exemptPounds
            groupPounds
            capped
            declaration {
              ...EpisodicManifestExemptionFields
            }
            calculated {
              pounds
              containerCount
              unweighedContainerCount
              containers {
                container {
                  id
                  containerLabel
                }
                pounds
                quantity
                unit
              }
            }
          }
        }
      }
    }
  }
`);

export const EPISODIC_MANIFEST_CANDIDATES_QUERY = gql(`
  query EpisodicManifestCandidates($id: ID!, $search: String) {
    episodicEvent(id: $id) {
      id
      manifestCandidates(search: $search) {
        manifestTrackingNumber
        documentKind
        shippedDate
        inSuggestionWindow
        codeOverlapCount
        groupPounds
        streams {
          wasteStream {
            id
            name
          }
          firstShipped
          groupPounds
          declarable
          matchingWasteCodes
          calculatedPoundsForEvent
          exemptPoundsOnOtherEvents
          existingExemptions {
            ...EpisodicManifestExemptionFields
          }
        }
      }
    }
  }
`);

export const SET_EPISODIC_MANIFEST_EXEMPTIONS_MUTATION = gql(`
  mutation SetEpisodicManifestExemptions(
    $episodicEventId: ID!
    $manifestTrackingNumber: String!
    $streams: [EpisodicStreamExemptionInput!]!
  ) {
    setEpisodicManifestExemptions(
      episodicEventId: $episodicEventId
      manifestTrackingNumber: $manifestTrackingNumber
      streams: $streams
    ) {
      id
    }
  }
`);

export const REMOVE_EPISODIC_MANIFEST_EXEMPTION_MUTATION = gql(`
  mutation RemoveEpisodicManifestExemption($exemptionId: ID!) {
    removeEpisodicManifestExemption(exemptionId: $exemptionId) {
      id
    }
  }
`);

export const PICKABLE_EPISODIC_EVENT_FRAGMENT = gql(`
  fragment PickableEpisodicEvent on WasteEpisodicEvent {
    id
    facilityId
    type
    status
    reason
    startDate
    endDate
    declarationsWritable
    relief {
      isAvailable
    }
  }
`);

export const EPISODIC_EVENTS_FOR_MANIFEST_QUERY = gql(`
  query EpisodicEventsForManifest($facilityId: ID!, $membersSearch: String!) {
    facility(id: $facilityId) {
      id
      name
      episodicEvents(pageSize: 100) {
        items {
          ...PickableEpisodicEvent
        }
      }
    }
    facilities(search: $membersSearch, page: 0, pageSize: 100) {
      items {
        id
        name
        episodicEvents(pageSize: 100) {
          items {
            ...PickableEpisodicEvent
          }
        }
      }
    }
  }
`);
